/**
 * Pantalla 5 (docs/FASE-1.md §6): suscripciones de una organización, alcanzadas desde
 * `OrganizationDetail` (pestaña "Suscripciones"). Cambiar plan, asientos, overrides
 * (`features`/`limits`) y cancelar; muestra el `EntitlementsSnapshot` resultante.
 */
import type {
  EntitlementsSnapshot,
  FeatureValue,
  Module,
  Plan,
  Subscription,
  SubscriptionStatus,
  UpsertSubscriptionInput,
} from "@continnua/contracts";
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from "react";

import { entitlementsApi, plansApi, subscriptionsApi } from "../api/client";
import { Badge, toneForStatus } from "../components/Badge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { Field } from "../components/Field";
import { Modal } from "../components/Modal";
import { toast } from "../components/Toast";
import { toDisplayMessage } from "../lib/errors";
import { formatCurrency, formatDate, formatDateTime, formatNumber } from "../lib/format";

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  trialing: "En prueba",
  active: "Activa",
  past_due: "Pago pendiente",
  suspended: "Suspendida",
  cancelled: "Cancelada",
};

const STATUS_OPTIONS: SubscriptionStatus[] = ["trialing", "active", "past_due", "suspended", "cancelled"];

// --- Alta / edición de suscripción --------------------------------------------------------------

function FeatureOverrideRow({
  featureKey,
  name,
  valueType,
  checked,
  value,
  onToggle,
  onChange,
}: {
  featureKey: string;
  name: string;
  valueType: "boolean" | "number" | "string";
  checked: boolean;
  value: FeatureValue;
  onToggle: (checked: boolean) => void;
  onChange: (value: FeatureValue) => void;
}): ReactElement {
  const inputId = `override-feature-${featureKey}`;
  return (
    <div className="override-row">
      <label className="checkbox-label" htmlFor={`${inputId}-toggle`}>
        <input id={`${inputId}-toggle`} type="checkbox" checked={checked} onChange={(e) => onToggle(e.target.checked)} />
        {name}
      </label>
      {checked && valueType === "boolean" && (
        <label className="checkbox-label" htmlFor={inputId}>
          <input id={inputId} type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
          Activado
        </label>
      )}
      {checked && valueType === "number" && (
        <input
          id={inputId}
          type="number"
          value={typeof value === "number" ? value : 0}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      )}
      {checked && valueType === "string" && (
        <input id={inputId} type="text" value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function LimitOverrideRow({
  limitKey,
  name,
  unit,
  checked,
  value,
  onToggle,
  onChange,
}: {
  limitKey: string;
  name: string;
  unit: string;
  checked: boolean;
  value: number | null;
  onToggle: (checked: boolean) => void;
  onChange: (value: number | null) => void;
}): ReactElement {
  const inputId = `override-limit-${limitKey}`;
  return (
    <div className="override-row">
      <label className="checkbox-label" htmlFor={`${inputId}-toggle`}>
        <input id={`${inputId}-toggle`} type="checkbox" checked={checked} onChange={(e) => onToggle(e.target.checked)} />
        {name} ({unit})
      </label>
      {checked && (
        <input
          id={inputId}
          type="number"
          min={0}
          placeholder="Ilimitado"
          value={value === null ? "" : value}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        />
      )}
    </div>
  );
}

function UpsertSubscriptionModal({
  orgId,
  modules,
  existing,
  onClose,
  onSaved,
}: {
  orgId: string;
  modules: readonly Module[];
  existing: Subscription | null;
  onClose: () => void;
  onSaved: (subscription: Subscription) => void;
}): ReactElement {
  const [moduleCode, setModuleCode] = useState(existing?.moduleCode ?? modules[0]?.code ?? "");
  const module = modules.find((m) => m.code === moduleCode) ?? modules[0];

  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [planCode, setPlanCode] = useState(existing?.planCode ?? "");
  const [seatsUnlimited, setSeatsUnlimited] = useState(existing ? existing.seats === null : true);
  const [seats, setSeats] = useState(existing?.seats ? String(existing.seats) : "1");
  const [status, setStatus] = useState<SubscriptionStatus>(existing?.status ?? "trialing");
  const [trialEndsAt, setTrialEndsAt] = useState(existing?.trialEndsAt ? existing.trialEndsAt.slice(0, 10) : "");
  const [featureOverrides, setFeatureOverrides] = useState<Record<string, FeatureValue>>({});
  const [limitOverrides, setLimitOverrides] = useState<Record<string, number | null>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!module) return;
    let cancelled = false;
    setLoadingPlans(true);
    plansApi
      .list(module.code)
      .then((page) => {
        if (cancelled) return;
        setPlans(page.items);
        setPlanCode((prev) => (prev && page.items.some((p) => p.code === prev) ? prev : (page.items[0]?.code ?? "")));
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(toDisplayMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingPlans(false);
      });
    return () => {
      cancelled = true;
    };
  }, [module]);

  useEffect(() => {
    setFeatureOverrides({});
    setLimitOverrides({});
  }, [module]);

  if (!module) {
    return (
      <Modal title="Nueva suscripción" onClose={onClose}>
        <p className="field-error" role="alert">
          No hay módulos disponibles.
        </p>
      </Modal>
    );
  }
  const selectedModule = module;

  function toggleFeature(key: string, checked: boolean, defaultValue: FeatureValue): void {
    setFeatureOverrides((prev) => {
      const next = { ...prev };
      if (checked) next[key] = prev[key] ?? defaultValue;
      else delete next[key];
      return next;
    });
  }

  function toggleLimit(key: string, checked: boolean, defaultValue: number | null): void {
    setLimitOverrides((prev) => {
      const next = { ...prev };
      if (checked) next[key] = key in prev ? prev[key]! : defaultValue;
      else delete next[key];
      return next;
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const input: UpsertSubscriptionInput = {
        planCode,
        seats: seatsUnlimited ? null : Number(seats),
        status,
        trialEndsAt: trialEndsAt ? new Date(trialEndsAt).toISOString() : null,
        overrides: { features: featureOverrides, limits: limitOverrides },
      };
      const subscription = await subscriptionsApi.upsert(orgId, selectedModule.code, input);
      onSaved(subscription);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={`${existing ? "Editar" : "Nueva"} suscripción · ${module.name}`} onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        {!existing && modules.length > 1 && (
          <Field label="Módulo">
            {(props) => (
              <select {...props} value={moduleCode} onChange={(e) => setModuleCode(e.target.value)}>
                {modules.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        <Field label="Plan">
          {(props) =>
            loadingPlans ? (
              <p className="subtle">Cargando planes…</p>
            ) : (
              <select {...props} required value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
                {plans.length === 0 && <option value="">No hay planes para este módulo</option>}
                {plans.map((plan) => (
                  <option key={plan.code} value={plan.code}>
                    {plan.name} · {formatCurrency(plan.priceMonthly, plan.currency)}
                  </option>
                ))}
              </select>
            )
          }
        </Field>
        <Field label="Estado">
          {(props) => (
            <select {...props} value={status} onChange={(e) => setStatus(e.target.value as SubscriptionStatus)}>
              {STATUS_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {STATUS_LABEL[value]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Asientos">
          {(props) => (
            <div className="inline-fields">
              <label className="checkbox-label" htmlFor="seats-unlimited">
                <input
                  id="seats-unlimited"
                  type="checkbox"
                  checked={seatsUnlimited}
                  onChange={(e) => setSeatsUnlimited(e.target.checked)}
                />
                Ilimitados
              </label>
              {!seatsUnlimited && <input {...props} type="number" min={0} value={seats} onChange={(e) => setSeats(e.target.value)} />}
            </div>
          )}
        </Field>
        <Field label="Fin del periodo de prueba" hint="Opcional.">
          {(props) => <input {...props} type="date" value={trialEndsAt} onChange={(e) => setTrialEndsAt(e.target.value)} />}
        </Field>

        {module.features.length > 0 && (
          <fieldset className="roles-fieldset">
            <legend>Overrides de features (sobre el plan)</legend>
            {module.features.map((feature) => (
              <FeatureOverrideRow
                key={feature.key}
                featureKey={feature.key}
                name={feature.name}
                valueType={feature.valueType}
                checked={feature.key in featureOverrides}
                value={featureOverrides[feature.key] ?? false}
                onToggle={(checked) => toggleFeature(feature.key, checked, feature.valueType === "boolean" ? true : feature.valueType === "number" ? 0 : "")}
                onChange={(value) => setFeatureOverrides((prev) => ({ ...prev, [feature.key]: value }))}
              />
            ))}
          </fieldset>
        )}

        {module.limits.length > 0 && (
          <fieldset className="roles-fieldset">
            <legend>Overrides de límites (sobre el plan)</legend>
            {module.limits.map((limit) => (
              <LimitOverrideRow
                key={limit.key}
                limitKey={limit.key}
                name={limit.name}
                unit={limit.unit}
                checked={limit.key in limitOverrides}
                value={limitOverrides[limit.key] ?? null}
                onToggle={(checked) => toggleLimit(limit.key, checked, null)}
                onChange={(value) => setLimitOverrides((prev) => ({ ...prev, [limit.key]: value }))}
              />
            ))}
          </fieldset>
        )}

        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="button-primary" disabled={submitting || !planCode}>
            {submitting ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// --- Entitlements resultantes --------------------------------------------------------------------

function EntitlementsModal({ orgId, moduleCode, onClose }: { orgId: string; moduleCode: string; onClose: () => void }): ReactElement {
  const [snapshot, setSnapshot] = useState<EntitlementsSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    entitlementsApi
      .get(orgId, moduleCode)
      .then((data) => {
        if (!cancelled) setSnapshot(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(toDisplayMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, moduleCode]);

  return (
    <Modal title={`Entitlements · ${moduleCode}`} onClose={onClose}>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {!snapshot && !error && <p>Cargando…</p>}
      {snapshot && (
        <div>
          <p>
            <Badge tone={snapshot.active ? "success" : "danger"}>{snapshot.active ? "Activo" : "Inactivo"}</Badge>{" "}
            {snapshot.plan && (
              <>
                Plan <strong>{snapshot.plan.name}</strong> ({snapshot.plan.billingModel === "flat" ? "tarifa plana" : "por asiento"})
              </>
            )}
          </p>
          <p className="subtle">
            Asientos: {formatNumber(snapshot.seats)} · Versión: {snapshot.version} · Fin de periodo: {formatDate(snapshot.currentPeriodEnd)}
          </p>
          <h3>Features</h3>
          {Object.keys(snapshot.features).length === 0 ? (
            <p className="subtle">Sin features habilitadas.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Clave</th>
                  <th scope="col">Valor</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(snapshot.features).map(([key, value]) => (
                  <tr key={key}>
                    <td>{key}</td>
                    <td>{String(value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <h3>Límites</h3>
          {Object.keys(snapshot.limits).length === 0 ? (
            <p className="subtle">Sin límites configurados.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Clave</th>
                  <th scope="col">Uso</th>
                  <th scope="col">Máximo</th>
                  <th scope="col">Periodo</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(snapshot.limits).map(([key, state]) => (
                  <tr key={key}>
                    <td>{key}</td>
                    <td>{formatNumber(state.used)}</td>
                    <td>{formatNumber(state.max)}</td>
                    <td>{state.period}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="subtle">Generado: {formatDateTime(snapshot.generatedAt)}</p>
        </div>
      )}
    </Modal>
  );
}

// --- Página / pestaña -------------------------------------------------------------------------

export function OrganizationSubscriptions({ orgId, modules }: { orgId: string; modules: readonly Module[] }): ReactElement {
  const [items, setItems] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Subscription | null>(null);
  const [viewingEntitlements, setViewingEntitlements] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<Subscription | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await subscriptionsApi.list(orgId);
      setItems(page.items);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  function moduleOf(code: string): Module | undefined {
    return modules.find((m) => m.code === code);
  }

  const subscribedModuleCodes = new Set(items.filter((s) => s.status !== "cancelled").map((s) => s.moduleCode));
  const availableModules = modules.filter((m) => !subscribedModuleCodes.has(m.code));

  const columns: DataTableColumn<Subscription>[] = [
    { key: "module", header: "Módulo", render: (s) => moduleOf(s.moduleCode)?.name ?? s.moduleCode },
    { key: "plan", header: "Plan", render: (s) => s.planCode },
    { key: "status", header: "Estado", render: (s) => <Badge tone={toneForStatus(s.status)}>{STATUS_LABEL[s.status]}</Badge> },
    { key: "seats", header: "Asientos", render: (s) => formatNumber(s.seats) },
    { key: "period", header: "Fin de periodo", render: (s) => formatDate(s.currentPeriodEnd) },
    {
      key: "actions",
      header: "Acciones",
      render: (s) => (
        <div className="button-row">
          <button type="button" className="button-secondary button-small" onClick={() => setViewingEntitlements(s.moduleCode)}>
            Entitlements
          </button>
          {s.status !== "cancelled" && (
            <>
              <button type="button" className="button-secondary button-small" onClick={() => setEditing(s)}>
                Cambiar
              </button>
              <button type="button" className="button-danger button-small" onClick={() => setCancelling(s)}>
                Cancelar
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <section>
      <div className="page-header">
        <h2>Suscripciones</h2>
        <button type="button" className="button-primary" onClick={() => setShowAdd(true)} disabled={availableModules.length === 0}>
          Nueva suscripción
        </button>
      </div>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      <DataTable columns={columns} rows={items} rowKey={(s) => s.id} emptyMessage={loading ? "Cargando…" : "Sin suscripciones."} />

      {showAdd && availableModules.length > 0 && (
        <UpsertSubscriptionModal
          orgId={orgId}
          modules={availableModules}
          existing={null}
          onClose={() => setShowAdd(false)}
          onSaved={(subscription) => {
            setShowAdd(false);
            setItems((prev) => [subscription, ...prev]);
            toast.success("Suscripción creada.");
          }}
        />
      )}
      {editing && moduleOf(editing.moduleCode) && (
        <UpsertSubscriptionModal
          orgId={orgId}
          modules={[moduleOf(editing.moduleCode) as Module]}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={(subscription) => {
            setEditing(null);
            setItems((prev) => prev.map((s) => (s.id === subscription.id ? subscription : s)));
            toast.success("Suscripción actualizada.");
          }}
        />
      )}
      {viewingEntitlements && <EntitlementsModal orgId={orgId} moduleCode={viewingEntitlements} onClose={() => setViewingEntitlements(null)} />}
      {cancelling && (
        <ConfirmDialog
          title="Cancelar suscripción"
          message={`¿Cancelar la suscripción de ${moduleOf(cancelling.moduleCode)?.name ?? cancelling.moduleCode}?`}
          confirmLabel="Cancelar suscripción"
          danger
          onCancel={() => setCancelling(null)}
          onConfirm={async () => {
            try {
              const updated = await subscriptionsApi.cancel(orgId, cancelling.moduleCode);
              setItems((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
              setCancelling(null);
              toast.success("Suscripción cancelada.");
            } catch (err) {
              toast.error(toDisplayMessage(err));
            }
          }}
        />
      )}
    </section>
  );
}
